import { supabase } from "./supabase-client";

async function resolveAuthToken(authToken?: string) {
  console.log("[api-certificates][resolveAuthToken] authToken recebido:", authToken ? `${authToken.slice(0, 12)}...${authToken.slice(-8)}` : "undefined");
  if (authToken) {
    console.log("[api-certificates][resolveAuthToken] usando authToken passado como parâmetro");
    return authToken;
  }

  console.log("[api-certificates][resolveAuthToken] nenhum authToken passado, buscando via supabase.auth.getSession()...");
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error("[api-certificates][resolveAuthToken] erro ao buscar sessão:", error.message);
  }
  console.log("[api-certificates][resolveAuthToken] sessão obtida:", !!session, "token:", session?.access_token ? `${session.access_token.slice(0, 12)}...${session.access_token.slice(-8)}` : "null");
  return session?.access_token ?? null;
}

async function tryParseErrorBody(error: unknown): Promise<Record<string, unknown> | null> {
  const context = (error as any)?.context;

  if (context instanceof Response) {
    try {
      return await context.clone().json();
    } catch {
      try {
        const text = await context.clone().text();
        return JSON.parse(text);
      } catch {
        return null;
      }
    }
  }

  if (context?.body) {
    const body = context.body;
    if (typeof body === "string") {
      try {
        return JSON.parse(body);
      } catch {
        return null;
      }
    }

    if (body instanceof Uint8Array) {
      try {
        return JSON.parse(new TextDecoder().decode(body));
      } catch {
        return null;
      }
    }
  }

  return null;
}

export async function verifyCertificatePublic(certCode: string) {
  const { data, error } = await supabase
    .from("certificates")
    .select("*, events(title, start_date, venue_name, producer_id)")
    .eq("certificate_code", certCode.trim())
    .maybeSingle();

  if (error) throw error;

  if (!data) {
    return { valid: false, message: "Certificado não encontrado. Verifique o código e tente novamente." };
  }

  const isRevoked = !!data.revoked_at;
  return {
    valid: !isRevoked,
    revoked: isRevoked,
    message: isRevoked ? "Certificado revogado." : "Certificado válido.",
    eventName: data.events?.title,
    participantName: data.attendee_name,
    issuedAt: data.issued_at,
    revokedAt: data.revoked_at,
    revokedReason: data.revoked_reason,
  };
}

export async function invokeAuthenticatedFunction<TResult = unknown, TBody extends Record<string, unknown> = Record<string, unknown>>(
  functionName: string,
  body: TBody,
  authToken?: string,
): Promise<TResult> {
  console.log(`[invokeAuthenticatedFunction][${functionName}] === INÍCIO ===`);
  console.log(`[invokeAuthenticatedFunction][${functionName}] functionName:`, functionName);
  console.log(`[invokeAuthenticatedFunction][${functionName}] authToken param existe:`, !!authToken);

  const resolvedAuthToken = await resolveAuthToken(authToken);

  if (!resolvedAuthToken) {
    console.error(`[invokeAuthenticatedFunction][${functionName}] ERRO: token resolvido é null/undefined`);
    throw new Error("Sessão expirada. Faça login novamente.");
  }

  const tokenPreview = `${resolvedAuthToken.slice(0, 12)}...${resolvedAuthToken.slice(-8)}`;
  console.log(`[invokeAuthenticatedFunction][${functionName}] tokenPreview:`, tokenPreview);
  console.log(`[invokeAuthenticatedFunction][${functionName}] body (sem token):`, JSON.stringify(body, null, 2));

  const requestBody = { ...body, accessToken: resolvedAuthToken };
  console.log(`[invokeAuthenticatedFunction][${functionName}] requestBody com accessToken:`, JSON.stringify({ ...requestBody, accessToken: "[REDACTED]" }, null, 2));

  console.log(`[invokeAuthenticatedFunction][${functionName}] chamando supabase.functions.invoke...`);

  try {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: requestBody,
    });

    if (error) {
      console.error(`[invokeAuthenticatedFunction][${functionName}] supabase.functions.invoke retornou error:`, error);
      console.error(`[invokeAuthenticatedFunction][${functionName}] error.name:`, error.name);
      console.error(`[invokeAuthenticatedFunction][${functionName}] error.message:`, error.message);
      console.error(`[invokeAuthenticatedFunction][${functionName}] error.context:`, (error as any)?.context);

      // Tenta extrair o payload de erro da resposta
      const errorPayload = await tryParseErrorBody(error);
      console.error(`[invokeAuthenticatedFunction][${functionName}] errorPayload:`, errorPayload);

      const detail = errorPayload?.message || errorPayload?.error || error.message || "Erro desconhecido na Edge Function";
      throw new Error(`Falha ao chamar função: ${detail}`);
    }

    console.log(`[invokeAuthenticatedFunction][${functionName}] response data:`, JSON.stringify(data, null, 2));
    console.log(`[invokeAuthenticatedFunction][${functionName}] === FIM (sucesso) ===`);

    return data as TResult;
  } catch (err: any) {
    // Se já for um Error nosso, propaga
    if (err instanceof Error && err.message.startsWith("Falha ao chamar função:")) {
      throw err;
    }

    // Erro inesperado (network, etc)
    console.error(`[invokeAuthenticatedFunction][${functionName}] exceção inesperada:`, err);
    throw new Error(`Falha ao chamar função: ${err?.message || "Erro desconhecido"}`);
  }
}

export async function bypassTicketFeedback(ticketId: string): Promise<{
  ok: boolean;
  ticket_id: string;
  certificate_id?: string;
  certificate_code?: string;
}> {
  const { data, error } = await supabase.rpc("bypass_ticket_feedback", {
    p_ticket_id: ticketId,
  });
  if (error) throw error;
  return data as unknown as {
    ok: boolean;
    ticket_id: string;
    certificate_id?: string;
    certificate_code?: string;
  };
}
